/**
 * ENG-6139 — read-only Composio connected-account binding probe.
 *
 * Verifies that a managed (Composio) integration's connected account is alive
 * AND bound to the `user_id` the agent runtime queries with. This is the signal
 * an MCP `tools/list` handshake CANNOT give: Composio returns a toolkit's tool
 * list even when no connected account exists for the entity — only tool *calls*
 * fail with `No connected account found for user ID … for toolkit …`. So a
 * dead/mis-bound account reads green on a handshake-only probe (the live
 * sherlock incident). This probe closes that gap.
 *
 * Mirrors the proven on-demand check in
 * `packages/api/src/lib/providers/composio-adapter.ts:checkConnectionDetailed`
 * + the verdict logic in `integrations.ts` (POST /integrations/:id/test), kept
 * dependency-free here so the manager-CLI host-side executor and any central
 * caller share one implementation. `fetch` is injected for unit-testing.
 *
 * Read-only: a single GET against the connected-accounts endpoint. No mutations.
 *
 * Outcome mapping (ConnectivityStatus vocabulary):
 *   - 'ok'              — account exists, ACTIVE, and bound to the expected user_id
 *   - 'down'            — not found / non-ACTIVE / bound to a different user_id
 *                         (mis-bound) / no binding returned → tool calls will fail
 *   - 'transient_error' — network/timeout or 5xx → retryable, don't escalate yet
 */

import type { ConnectivityProbeOutcome } from './connectivity-probe.js';
import { assessAuthConfigLinkage } from './composio-linkage.js';

const PROBE_TIMEOUT_MS = 10_000;
const COMPOSIO_API_BASE = 'https://backend.composio.dev';

export interface ComposioAccountProbeParams {
  /** The recorded `connected_account_id` (e.g. `ca_…`). */
  connectedAccountId: string;
  /** Composio project API key sent as `x-api-key`. */
  apiKey: string;
  /** The entity the agent runtime queries with, e.g. `${orgId}:${agentId}`. */
  expectedUserId: string;
  /**
   * ENG-6157: the agent's wired MCP server id (`composio_server_id`). When
   * provided, the probe additionally asserts the account's auth_config is one
   * the server resolves with — catching the false green where the account is
   * healthy but the server points at a different auth_config. Omit to keep the
   * account-only check (ENG-6139 behaviour).
   */
  serverId?: string;
  /** Override the Composio API base (tests / self-host). */
  apiBase?: string;
}

async function timedFetch(
  fetchImpl: typeof fetch,
  url: string,
  init: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    return await fetchImpl(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Probe a Composio connected account's liveness + binding.
 *
 * Returns a {@link ConnectivityProbeOutcome}; never throws (network/parse
 * failures map to `transient_error` so a blip can't flip an integration to a
 * false `down`).
 */
export async function probeComposioAccount(
  params: ComposioAccountProbeParams,
  fetchImpl: typeof fetch = fetch,
): Promise<ConnectivityProbeOutcome> {
  const { connectedAccountId, apiKey, expectedUserId } = params;
  const base = params.apiBase ?? COMPOSIO_API_BASE;

  if (!connectedAccountId) {
    return {
      status: 'down',
      message: 'No connected account recorded — reconnect required',
    };
  }
  if (!apiKey || !expectedUserId) {
    // Can't perform the check honestly — treat as retryable rather than a
    // false down (a missing key/entity is a wiring problem, not a dead account).
    return {
      status: 'transient_error',
      message: 'Composio probe missing api key or expected user_id',
    };
  }

  let res: Response;
  try {
    res = await timedFetch(
      fetchImpl,
      `${base}/api/v3/connected_accounts/${encodeURIComponent(connectedAccountId)}`,
      { headers: { 'x-api-key': apiKey } },
    );
  } catch (err) {
    const isAbort = (err as Error)?.name === 'AbortError';
    return {
      status: 'transient_error',
      message: isAbort
        ? `Composio probe timed out after ${PROBE_TIMEOUT_MS / 1000}s`
        : `Composio probe failed: ${(err as Error).message}`,
    };
  }

  if (!res.ok) {
    // 5xx → retryable; 4xx (incl. 404 deleted/revoked) → the account is gone.
    if (res.status >= 500) {
      return {
        status: 'transient_error',
        message: `Composio unreachable (HTTP ${res.status}) — retrying`,
        details: { connectedAccountId, httpStatus: res.status },
      };
    }
    return {
      status: 'down',
      message: `Composio account ${connectedAccountId} not found (HTTP ${res.status}) — reconnect required`,
      details: { connectedAccountId, httpStatus: res.status },
    };
  }

  let data: { user_id?: string; status?: string; auth_config_id?: string; auth_config?: { id?: string } };
  try {
    data = (await res.json()) as {
      user_id?: string;
      status?: string;
      auth_config_id?: string;
      auth_config?: { id?: string };
    };
  } catch (err) {
    return {
      status: 'transient_error',
      message: `Composio probe response unparseable: ${(err as Error).message}`,
    };
  }

  const accountStatus = data.status ?? 'unknown';
  if (accountStatus !== 'ACTIVE') {
    return {
      status: 'down',
      message: `Composio account ${connectedAccountId} status=${accountStatus} — reconnect required`,
      details: { connectedAccountId, status: accountStatus, boundUserId: data.user_id ?? null },
    };
  }

  const boundUserId = data.user_id;
  if (!boundUserId) {
    // ACTIVE but no binding returned — can't prove the agent's query will
    // resolve. Same fail-closed stance as the Test endpoint.
    return {
      status: 'down',
      message:
        `Composio account ${connectedAccountId} is ACTIVE but returned no user_id binding — ` +
        `runtime queries as '${expectedUserId}', so tool calls can't be confirmed`,
      details: { connectedAccountId, status: accountStatus, boundUserId: null, expectedUserId },
    };
  }

  if (boundUserId !== expectedUserId) {
    return {
      status: 'down',
      message:
        `Composio account ${connectedAccountId} is bound to user_id '${boundUserId}' but the agent ` +
        `runtime queries as '${expectedUserId}' — tool calls will fail. Reconnect to bind correctly.`,
      details: { connectedAccountId, status: accountStatus, boundUserId, expectedUserId },
    };
  }

  // ENG-6157: account is ACTIVE + bound to the right entity. If the caller gave
  // us the wired server id, also assert the auth_config linkage — the account
  // can be perfectly healthy yet unreachable because the server resolves a
  // different auth_config than the account was created under.
  const accountAuthConfigId = data.auth_config_id ?? data.auth_config?.id;
  if (params.serverId) {
    const serverAuthConfigIds = await fetchServerAuthConfigIds(
      fetchImpl,
      base,
      params.serverId,
      apiKey,
    );
    const linkage = assessAuthConfigLinkage({
      accountAuthConfigId,
      serverAuthConfigIds,
      serverId: params.serverId,
    });
    if (linkage.linked === false) {
      return {
        status: 'down',
        message: linkage.message,
        details: { connectedAccountId, status: accountStatus, boundUserId, ...linkage.details },
      };
    }
  }

  return {
    status: 'ok',
    message: `Connected (account ${connectedAccountId}, status=ACTIVE)`,
    details: {
      connectedAccountId,
      status: accountStatus,
      boundUserId,
      ...(accountAuthConfigId ? { authConfigId: accountAuthConfigId } : {}),
    },
  };
}

/**
 * ENG-6157: read the auth_config ids the given MCP server resolves with.
 * Returns `null` on any fetch FAILURE (non-2xx / network / parse) so the
 * linkage check stays indeterminate rather than a false mismatch; `[]` when the
 * server genuinely binds no auth config (a real broken linkage).
 */
async function fetchServerAuthConfigIds(
  fetchImpl: typeof fetch,
  base: string,
  serverId: string,
  apiKey: string,
): Promise<string[] | null> {
  let res: Response;
  try {
    res = await timedFetch(
      fetchImpl,
      `${base}/api/v3/mcp/${encodeURIComponent(serverId)}`,
      { headers: { 'x-api-key': apiKey } },
    );
  } catch {
    return null;
  }
  if (!res.ok) return null;
  try {
    const data = (await res.json()) as {
      auth_config_ids?: string[];
      auth_configs?: Array<{ id?: string }>;
    };
    return (
      data.auth_config_ids
      ?? data.auth_configs?.map((c) => c.id).filter((id): id is string => typeof id === 'string' && id.length > 0)
      ?? []
    );
  } catch {
    return null;
  }
}
